import CONFIG from './config';
import {
  BROWSER_WEBVIEW_CREATED_EVENT,
  sendCustomTrack
} from './TrackAnalyticsHelper';
import { getMaidUrl } from './mraid-utils';
import { isFeatureEnable } from './features-enabling';

const BROWSER_PARAMS = [
  'browser_keep_alive',
  'browser',
  'analytics_enable',
  'use_recommended_links',
  'recommended_urls',
  'recommended_urls_timeout',
  'recommended_links_image',
  'recommended_links_title',
  'recommended_links_text',
  'recommended_links_acd',
  'recommended_links_nobounce',
  'recommended_links_translations',
  'recommended_links_display_close_button',
  'recommended_links_trigger_url_regex',
  'recommended_links_loader_timeout',
  'recommended_links_loader_close_url_regex',
  'recommended_links_loader_image',
  'recommended_links_use_loader',
  'recommended_links_loader_color',
  'recommended_links_redirect_url',
  'recommended_links_close_btn_size',
  'recommended_links_use_fullscreen',
  'recommended_links_close_btn_color',
  'recommended_links_progressbar_color',
  'use_content_discovery',
  'content_discovery_title',
  'content_discovery_acd',
  'content_discovery_translations',
  'content_discovery_display_close_button',
  'content_discovery_trigger_url_regex',
  'content_discovery_loader_timeout',
  'content_discovery_loader_close_url_regex',
  'content_discovery_loader_image',
  'content_discovery_use_loader',
  'content_discovery_loader_color',
  'content_discovery_redirect_url',
  'content_discovery_close_btn_size',
  'content_discovery_use_fullscreen',
  'content_discovery_close_btn_color',
  'content_discovery_use_countdown_close_button',
  'content_discovery_close_button_delay',
  'content_discovery_text_1',
  'content_discovery_image_1',
  'content_discovery_nobounce_1',
  'content_discovery_text_2',
  'content_discovery_image_2',
  'content_discovery_nobounce_2',
  'content_discovery_text_3',
  'content_discovery_image_3',
  'content_discovery_nobounce_3',
  'content_discovery_object_fit',
  'content_discovery_blur_effect_show',
  'content_discovery_blur_effect_zoom',
  'content_discovery_blur_effect_offsetX',
  'content_discovery_blur_effect_offsetY',
  'browser_translations',
  'browser_navbar_height',
  'browser_navbar_color',
  'browser_prevent_close_before_load',
  'browser_force_close_display_after',
  'browser_use_countdown_close_button',
  'browser_use_optout',
  'browser_loading_bar_color',
  'browser_loading_bar_height',
  'browser_controls_invert_order',
  'browser_controls_color',
  'browser_controls_size',
  'browser_controls_icon_size',
  'allow_icon_after_browser',
  'click_url',
  'use_icon',
  'icon_name',
  'icon',
  'identifier',
  'browser_set_bigger_navbar_for_appbundles',
  'browser_verizon_cart_redirect',
  'browser_verizon_cart_icon',
  'browser_save_icon',
  'default_browser_close_fix',
  'browser_navbar_title',
  'browser_navbar_title_color'
];

export const BROWSERS = {
  CHROME_TAB: 'chrometab',
  DEFAULT_BROWSER: 'defaultbrowser',
  OGURY_BROWSER: 'browser'
};

export interface BrowserParameterAccessor {
  getParameter: (key: string) => any;
}

const DEFAULT_BROWSER_MODES = new Set([
  BROWSERS.DEFAULT_BROWSER,
  BROWSERS.CHROME_TAB
]);
const ANDROID_INTENTS_FEATURE = 'androidIntents';
const IOS_DEFAULT_BROWSER_FEATURE = 'iosDefaultBrowser';

export function getBrowserMraidTag(adOptions: FormatOptions, url: string) {
  const browserUrl = CONFIG.SDK3_BROWSER_URL;
  const mraid = isFeatureEnable('injectMraidOnWebview')
    ? `<script src="${getMaidUrl()}"></script>`
    : '';

  return `
    ${mraid}
    <script>
      function waitFor(condition, cb) {
          var interval = setInterval(() => {
              if(condition()) {
                  clearInterval(interval);
                  cb();
              }
          }, 10);
      }

      function loadBrowser() {
        const head = document.getElementsByTagName('head').item(0);
        const js = document.createElement('script');
        js.setAttribute('type', 'text/javascript');
        js.setAttribute('src', '${browserUrl}');
        head.appendChild(js);
      }

      function init() {
        var params = ${JSON.stringify(
          BROWSER_PARAMS.reduce((res: AnyObject, key) => {
            res[key] = adOptions.getParameter(key);
            return res;
          }, {})
        )};

        params["browser_url"] = '${url}';

        function getParameter(key) {
          return params[key];
        }

        window.initFormat({
          getParameter: getParameter,
          context: ${JSON.stringify(adOptions.context)},
          container: document.body
        });
      }

      waitFor(() => (window.mraid && window.mraid.getState() === 'default'), () => {
          loadBrowser();
          waitFor(() => window.initFormat && window.mraid && window.mraid.getMaxSize(), () => {
              init();
          });
      });
    </script>
  `;
}

export function getBrowserHtmlContent(adOptions: FormatOptions, url: string) {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">
        <link rel="icon" type="image/png" href="data:image/png;base64,iVBORw0KGgo=">
      </head>
      <body>
        ${getBrowserMraidTag(adOptions, url)}
      </body>
    </html>
  `;
}

interface OpenBrowserOptions {
  forceDefault?: boolean;
}

export function openBrowser(
  adOptions: FormatOptions,
  url: string,
  options: OpenBrowserOptions = {}
) {
  const shouldOpenDefault = shouldOpenDefaultBrowser(adOptions, options);
  const useAndroidIntents = isFeatureEnable(ANDROID_INTENTS_FEATURE);
  const useIosDefaultBrowser = isFeatureEnable(IOS_DEFAULT_BROWSER_FEATURE);

  if (shouldOpenDefault && useAndroidIntents) {
    sendCustomTrack(BROWSER_WEBVIEW_CREATED_EVENT);
    const useChromeTab =
      adOptions?.getParameter('browser') === BROWSERS.CHROME_TAB;
    openDefaultBrowserIntent(url, { useChromeTab });
    return;
  }

  if (shouldOpenDefault && useIosDefaultBrowser) {
    sendCustomTrack(BROWSER_WEBVIEW_CREATED_EVENT);
    const mraid = window.mraid;
    const mraidOpen =
      window.mraidOriginalOpen ?? mraid?.open?.bind(mraid) ?? mraid?.open;
    mraidOpen?.(url);
    return;
  }

  if (options.forceDefault) {
    const getParameterRef = adOptions.getParameter;
    adOptions.getParameter = key => {
      if (key === 'browser') {
        return BROWSERS.DEFAULT_BROWSER;
      }
      return getParameterRef(key);
    };
  }

  // ogyCreateWebView / ogyUpdateWebView are Ogury-specific; on external SDK fallback to mraid.open to avoid errors
  if (!window.mraid.ogyCreateWebView || !window.mraid.ogyUpdateWebView) {
    sendCustomTrack(BROWSER_WEBVIEW_CREATED_EVENT);
    const mraidOpen =
      window.mraidOriginalOpen ??
      window.mraid?.open?.bind(window.mraid) ??
      window.mraid?.open;
    mraidOpen?.(url);
    return;
  }

  const keepAlive = adOptions.getParameter('browser_keep_alive');

  const browserProps = {
    content: getBrowserHtmlContent(adOptions, url),
    id: 'browser',
    keepAlive,
    position: { x: 0, y: 0 },
    size: window.mraid.getMaxSize()
  };
  window.mraid.ogyCreateWebView!(browserProps, (err, { webviewId }) => {
    sendCustomTrack(BROWSER_WEBVIEW_CREATED_EVENT);
    let {
      width: previousWidth,
      height: previousHeight
    } = window.mraid.getMaxSize();

    window.addEventListener('resize', () => {
      window.mraid.ogyUpdateWebView!(webviewId, {
        size: window.mraid.getMaxSize()
      });
    });

    window.mraid.addEventListener('sizeChange', (width, height) => {
      if (width !== previousWidth || height !== previousHeight) {
        previousHeight = height;
        previousWidth = width;
        window.mraid.ogyUpdateWebView!(webviewId, {
          size: { width, height }
        });
      }
    });
  });
}

interface DefaultBrowserIntentOptions {
  useChromeTab?: boolean;
  fallbackUrl?: string;
  onOpened?: () => void;
  onError?: (error?: Error | null) => void;
}

function buildIntentUri(
  url: string,
  useChromeTab: boolean,
  fallbackUrl?: string
): string {
  const schemeRegex = /^https?:/i;
  const schemeMatch = schemeRegex.exec(url);
  const scheme = schemeMatch ? schemeMatch[0].replace(':', '') : 'https';
  const path = url.replace(/^[a-z]+:\/\//i, '');
  let intent = `intent://${path}#Intent;scheme=${scheme}`;
  if (useChromeTab) {
    intent += ';S.android.support.customtabs.extra.SESSION=';
  }
  if (fallbackUrl) {
    intent += `;S.browser_fallback_url=${fallbackUrl}`;
  }
  intent += ';end';
  return intent;
}

export function openDefaultBrowserIntent(
  url: string,
  {
    useChromeTab = false,
    fallbackUrl,
    onOpened,
    onError
  }: DefaultBrowserIntentOptions = {}
) {
  const intentUri = buildIntentUri(url, useChromeTab, fallbackUrl);
  const startIntent =
    window.mraid?.ogyStartIntent ?? window.mraid?.ogyResolveIntent;

  if (startIntent) {
    startIntent(intentUri, (error?: Error | null) => {
      if (error) {
        console.warn(
          'Default browser intent failed, falling back to window.open',
          error
        );
        window.open(url, '_blank');
        onError?.(error);
        return;
      }
      onOpened?.();
    });
    return intentUri;
  }

  window.open(url, '_blank');
  onOpened?.();
  return intentUri;
}

export function shouldOpenDefaultBrowser(
  browserParams: BrowserParameterAccessor,
  options: OpenBrowserOptions = {}
): boolean {
  if (options.forceDefault) {
    return true;
  }

  const resolvedBrowserMode =
    typeof browserParams?.getParameter === 'function'
      ? browserParams.getParameter('browser')
      : (browserParams as any)?.browser;
  const normalizedBrowserMode = resolvedBrowserMode || BROWSERS.DEFAULT_BROWSER;
  return DEFAULT_BROWSER_MODES.has(normalizedBrowserMode);
}
